// Master switch for new account creation.
// While this is false, all sign-up entry points are hidden from the UI and only
// addresses on REGISTRATION_ALLOWED_DOMAINS can still create an account: the
// sign-up form and Google sign-in both refuse anybody else.
//
// Registration stays fully open on the test project; production
// (robowriter-3aa15) and any other project get the closed door.
//
// This is deliberately a client-side gate only -- user_authentication_handler
// still accepts create_user, so anyone calling the callable directly can sign
// up. It stops the public sign-up flow, it is not a security boundary.
export const REGISTRATION_ENABLED =
    process.env.REACT_APP_PROJECT_ID?.includes("tst-robowriter") ?? false;

// Domains that may still register while REGISTRATION_ENABLED is false. This keeps
// our own addresses working -- and with them the end-to-end suite, which signs up
// as no-reply+<timestamp>-<worker>@robowriter.de.
export const REGISTRATION_ALLOWED_DOMAINS = ["robowriter.de"];

// The /sign-up route stays reachable by direct link for as long as somebody may
// still register; it is only unlinked from the UI. An empty allow list closes it.
export const SIGN_UP_PAGE_REACHABLE =
    REGISTRATION_ENABLED || REGISTRATION_ALLOWED_DOMAINS.length > 0;

// While registration is closed, existing accounts may only sign in if they
// were active since this date. The actual check lives in the
// user_authentication_handler cloud function ("check_sign_in_allowed" action);
// this mirrors SIGN_IN_ACTIVITY_CUTOFF in python_functions/main.py and feeds
// the banner/toast copy.
export const SIGN_IN_ACTIVITY_CUTOFF_LABEL = "01.01.2026";

export const SIGN_IN_BLOCKED_MESSAGE =
    `Dieses Konto war seit dem ${SIGN_IN_ACTIVITY_CUTOFF_LABEL} nicht aktiv. Eine Anmeldung ist daher leider nicht mehr möglich.`;

/** Whether this address is still allowed to create a new account. */
export function isRegistrationAllowedForEmail(email?: string | null): boolean {
    if (REGISTRATION_ENABLED) return true;
    const trimmed = email?.trim().toLowerCase() ?? "";
    const at = trimmed.lastIndexOf("@");
    if (at < 0) return false;
    return REGISTRATION_ALLOWED_DOMAINS.includes(trimmed.slice(at + 1));
}

export enum UserGuideStepEnum {
    CREATE_ACCOUNT = 'CREATE_ACCOUNT',
    CHAT = 'CHAT',
    TONE = 'TONE',
    TEMPLATE = 'TEMPLATE',
    NEWSLETTER = 'NEWSLETTER'
}

export enum ModalTypeEnum {
    OnboardingModal = 'OnboardingModal',
    InviteUserModal = 'InviteUserModal',
    TestimonialModal = 'TestimonialModal',
    FeedbackModal = 'FeedbackModal'
}

export const completeOnBoardingStepMessagesMap = {
    [UserGuideStepEnum.CREATE_ACCOUNT]: '',
    [UserGuideStepEnum.CHAT]: 'Herzlichen Glückwunsch, Du hast zum ersten Mal erfolgreich den KI-Chat genutzt. Hier sind 3 Bonus-Texte für Dich 🎉',
    [UserGuideStepEnum.TONE]: 'Dein erster eigener Tonfall. Du kannst diesen nun jederzeit in einer der Vorlagen auswählen. Hier sind 3 Bonus-Texte für Dich. 🎉',
    [UserGuideStepEnum.TEMPLATE]: 'Du hast Deine erste eigene Vorlage erstellt. Hier sind 3 Bonus-Texte für Dich 🎉 ',
    [UserGuideStepEnum.NEWSLETTER]: '',
}

export const updateBonusMap = {
    [UserGuideStepEnum.CREATE_ACCOUNT]: '',
    [UserGuideStepEnum.CHAT]: 'ai_chat_used',
    [UserGuideStepEnum.TONE]: 'tonality_used',
    [UserGuideStepEnum.TEMPLATE]: 'template_used',
    [UserGuideStepEnum.NEWSLETTER]: '',
}